import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { CreateOrUpdateUser, MarkPaymentSetup, CreatePaymentSetupSession } from '../../Client/ManagementServiceClient';
import { getAuth } from 'firebase/auth';
import { LEGAL_PATHS, TERMS_VERSION } from '../Legal';
import './styles.css';

function UserProfile({ onProfileComplete, existingProfile }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [stripeSetupComplete, setStripeSetupComplete] = useState(false);
    const [showPaymentSetup, setShowPaymentSetup] = useState(false);
    const [stripeCustomerId, setStripeCustomerId] = useState(null);
    const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);
    const [formData, setFormData] = useState({
        UserName: '',
        UserId: '',
        HasAcceptedTerms: false,
        TermsVersion: TERMS_VERSION
    });

    useEffect(() => {
        if (existingProfile) {
            // Populate form with existing profile data
            setFormData({
                UserName: existingProfile.UserName || '',
                UserId: existingProfile.UserId || '',
                HasAcceptedTerms: existingProfile.HasAcceptedTerms || false,
                TermsVersion: existingProfile.TermsVersion || TERMS_VERSION
            });
            setTermsAccepted(existingProfile.HasAcceptedTerms || false);
            setStripeCustomerId(existingProfile.stripe_customer_id || null);
            
            // If customer exists but payment method not added, show payment setup
            if (existingProfile.stripe_customer_id && !existingProfile.stripe_payment_method_added) {
                setShowPaymentSetup(true);
                setStripeSetupComplete(false);
            } else {
                // Customer exists and payment method added, or no customer yet
                setStripeSetupComplete(!!existingProfile.stripe_payment_method_added);
            }
        } else if (user) {
            // New user - populate with Firebase auth data
            setFormData(prev => ({
                ...prev,
                UserName: user.displayName || '',
                UserId: user.uid
            }));
        }
    }, [user, existingProfile]);

    // Asks the backend whether payment is set up and, if so, moves the user on.
    // With a Checkout session id this uses authoritative SetupIntent verification;
    // if that comes back negative (or no session is available) it falls back to
    // asking Stripe whether a card is now attached to the customer. `silent`
    // suppresses the "no card yet" error for background polls.
    const runPaymentCheck = useCallback(async ({ sessionId = null, silent = false } = {}) => {
        try {
            setCheckingPaymentStatus(true);
            const auth = getAuth();
            if (!auth.currentUser) {
                throw new Error('Not signed in yet');
            }
            const idToken = await auth.currentUser.getIdToken();

            let result = await MarkPaymentSetup(idToken, sessionId);
            // Session verification failed: ask Stripe directly if a card is attached.
            if (result.status !== 200 && sessionId) {
                result = await MarkPaymentSetup(idToken);
            }

            if (result.status === 200) {
                setShowPaymentSetup(false);
                setStripeSetupComplete(true);
                setError('');
                if (onProfileComplete) {
                    onProfileComplete(result.myJson);
                }
                return true;
            }

            if (!silent) {
                setShowPaymentSetup(true);
                setStripeSetupComplete(false);
                setError(result.myJson?.detail || 'We couldn\'t find a payment method on file yet. Add a card, then check again.');
            }
            return false;
        } catch (err) {
            console.error('Error checking payment setup:', err);
            if (!silent) {
                setError('Failed to verify payment setup. Please try again.');
            }
            return false;
        } finally {
            setCheckingPaymentStatus(false);
        }
    }, [onProfileComplete]);

    // Ensures the Stripe return is verified at most once per page load, even as
    // the component re-renders (e.g. onProfileComplete changing identity).
    const paymentReturnHandledRef = useRef(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const paymentSetupStatus = params.get('payment_setup');
        const sessionId = params.get('session_id');

        if (!paymentSetupStatus || paymentReturnHandledRef.current) {
            return;
        }

        if (paymentSetupStatus === 'cancel') {
            paymentReturnHandledRef.current = true;
            setError('Payment setup was canceled. Please try again.');
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        if (paymentSetupStatus !== 'success') {
            return;
        }

        // Detection is driven by the URL signal + the customer id already on the
        // loaded profile — not by transient UI state that a separate effect has
        // to set first. Wait (without consuming the URL) until the profile with a
        // customer id is available, so a slow profile load can't drop the return.
        const customerId = existingProfile?.stripe_customer_id || stripeCustomerId;
        if (!customerId) {
            return;
        }

        // Claim the return and clear the query string up front so a reload or
        // re-render can't reprocess a stale session id.
        paymentReturnHandledRef.current = true;
        window.history.replaceState({}, document.title, window.location.pathname);
        setShowPaymentSetup(true);
        runPaymentCheck({ sessionId, silent: false });
    }, [existingProfile, stripeCustomerId, runPaymentCheck]);

    // Safety net for redirects that lose the return params: once the payment
    // screen is shown for a known customer, silently ask Stripe whether a card is
    // already attached (from a just-completed Checkout) and move on if so.
    const autoCheckedRef = useRef(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('payment_setup')) {
            return; // the URL-return effect owns this case
        }
        if (!showPaymentSetup || !stripeCustomerId || autoCheckedRef.current) {
            return;
        }
        autoCheckedRef.current = true;
        runPaymentCheck({ silent: true });
    }, [showPaymentSetup, stripeCustomerId, runPaymentCheck]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate username format
        const usernameRegex = /^[a-z0-9_]+$/;
        if (!usernameRegex.test(formData.UserName)) {
            setError('Username must contain only lowercase letters, numbers, and underscores.');
            return;
        }

        if (!termsAccepted) {
            setError('You must accept the terms and conditions to continue.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();

            const userData = {
                UserId: formData.UserId,
                UserName: formData.UserName,
                UserRole: 'User',
                IsActive: true,
                CreatedAt: new Date().toISOString(),
                UpdatedAt: new Date().toISOString(),
                WorkspaceCount: 0,
                TermsVersion: formData.TermsVersion,
                HasAcceptedTerms: termsAccepted,
                // Send the real login email so the backend can create the Stripe
                // customer with it (and prefill Checkout correctly) instead of
                // falling back to a lookup that may not carry an email.
                Email: user?.email || auth.currentUser?.email || ''
            };

            const result = await CreateOrUpdateUser(idToken, userData);

            console.log('User creation result:', result);

            if (result.status === 200 || result.status === 201) {
                // Profile created successfully
                console.log('Stripe customer ID:', result.myJson.stripe_customer_id);
                setStripeCustomerId(result.myJson.stripe_customer_id);
                
                // Billing is required, so a customer must exist to continue.
                if (result.myJson.stripe_customer_id) {
                    setShowPaymentSetup(true);
                } else {
                    // No customer was created (e.g. billing setup failed). Don't
                    // silently drop the user into the app past a required step —
                    // keep them here with an error so they can retry.
                    setError('We couldn\'t set up billing for your account. Please try again.');
                }
            } else if (result.status === 400) {
                const errorMessage = result.myJson?.detail || 'Please check your input and try again.';
                setError(errorMessage);
            } else if (result.status === 409) {
                setError('This username is already taken. Please choose another one.');
            } else {
                setError('Failed to create profile. Please try again.');
            }
        } catch (err) {
            console.error('Error creating user profile:', err);
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-profile-container">
            <div className="user-profile-card">
                <div className="profile-brand" aria-label="Vixo">vi<span className="profile-brand-x">x</span>o</div>
                <h2>{showPaymentSetup ? 'Add a payment method' : 'Create your account'}</h2>
                <p className="profile-subtitle">
                    {showPaymentSetup
                        ? "One last step — add a card and you're in. You're only billed for what you use."
                        : 'Pick a username and accept the terms to get started.'}
                </p>

                {!stripeSetupComplete && (
                    <div className="onboard-steps" aria-hidden="true">
                        <span className={`onboard-step ${showPaymentSetup ? 'done' : 'current'}`}>1 · Account</span>
                        <span className="onboard-step-sep">→</span>
                        <span className={`onboard-step ${showPaymentSetup ? 'current' : ''}`}>2 · Payment</span>
                    </div>
                )}

                {stripeSetupComplete && (
                    <div className="stripe-status-banner">Payment method connected</div>
                )}

                {!showPaymentSetup ? (
                    <form onSubmit={handleSubmit} className="profile-form">
                        <div className="form-group">
                            <label htmlFor="userName">Username</label>
                            <input
                                id="userName"
                                type="text"
                                value={formData.UserName}
                                onChange={(e) => setFormData({ ...formData, UserName: e.target.value.toLowerCase() })}
                                required
                                placeholder="e.g. maya_creates"
                                pattern="[a-z0-9_]+"
                                title="Username must contain only lowercase letters, numbers, and underscores"
                            />
                            <small className="field-hint">Lowercase letters, numbers, and underscores.</small>
                        </div>

                        <div className="terms-section">
                            <h3>Terms and Conditions</h3>
                            <div className="terms-content">
                                <p>In short, by using Vixo you agree that:</p>
                                <ul>
                                    <li>You keep ownership of what you upload and create.</li>
                                    <li>Your content is processed by AI providers to run the Service.</li>
                                    <li>You hold the rights to what you upload and publish.</li>
                                    <li>Billing is usage-based, metered, and charged in arrears.</li>
                                </ul>
                                <p>
                                    {/* Opened in a new tab so reading the documents does not
                                        discard the half-completed onboarding form. */}
                                    The full{' '}
                                    <a href={LEGAL_PATHS.terms} target="_blank" rel="noopener noreferrer">
                                        Terms of Service
                                    </a>{' '}
                                    and{' '}
                                    <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer">
                                        Privacy Policy
                                    </a>{' '}
                                    govern your use of Vixo.
                                </p>
                            </div>

                            <div className="checkbox-group">
                                <input
                                    id="termsCheckbox"
                                    type="checkbox"
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                    required
                                />
                                <label htmlFor="termsCheckbox">
                                    I have read and accept the Terms of Service and Privacy Policy
                                    (Version {formData.TermsVersion})
                                </label>
                            </div>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <button
                            type="submit"
                            className="submit-button"
                            disabled={loading || !termsAccepted}
                        >
                            {loading ? 'Setting up…' : stripeSetupComplete ? 'Save' : 'Continue to payment'}
                        </button>
                    </form>
                ) : (
                    <div className="payment-setup-section">
                        <h3>Secure checkout with Stripe</h3>
                        <p className="payment-setup-copy">
                            Add your card on Stripe's secure hosted page — we never see your card
                            details, and you're only billed for what you use.
                        </p>

                        {error && <div className="error-message">{error}</div>}

                        {checkingPaymentStatus ? (
                            <p className="payment-verifying">Checking your payment method…</p>
                        ) : (
                            <div className="payment-actions">
                                <button
                                    type="button"
                                    className="payment-primary-btn"
                                    disabled={loading}
                                    onClick={async () => {
                                        try {
                                            setLoading(true);
                                            const auth = getAuth();
                                            const idToken = await auth.currentUser.getIdToken();
                                            const result = await CreatePaymentSetupSession(idToken);
                                            if (result.status === 200 && result.myJson?.url) {
                                                window.location.href = result.myJson.url;
                                                return;
                                            }
                                            setError(result.myJson?.detail || 'Failed to open Stripe payment setup. Please try again.');
                                        } catch (err) {
                                            console.error('Error creating Stripe setup session:', err);
                                            setError('An error occurred while opening Stripe setup. Please try again.');
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                >
                                    {loading ? 'Opening Stripe…' : 'Add payment method'}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export { UserProfile };
